import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

// Batch delay in ms - accumulate updates before processing
const BATCH_DELAY_MS = 1000;

/**
 * Custom hook for real-time discrepancy updates with batched processing
 * @param {function} onDiscrepancyUpdate - Callback when a discrepancy is updated
 * @param {function} onDiscrepancyInsert - Callback when a new discrepancy is created
 * @param {function} onDiscrepancyDelete - Callback when a discrepancy is deleted
 * @param {boolean} enabled - Whether the subscription is enabled
 */
export const useRealtimeDiscrepancies = (
  onDiscrepancyUpdate,
  onDiscrepancyInsert = null,
  onDiscrepancyDelete = null,
  enabled = true
) => {
  // Refs for batching updates
  const pendingInserts = useRef(new Map());
  const pendingUpdates = useRef(new Map());
  const pendingDeletes = useRef(new Set());
  const batchTimeout = useRef(null);

  // Process all pending events in a single batch
  const processBatch = useCallback(async () => {
    const inserts = new Map(pendingInserts.current);
    const updates = new Map(pendingUpdates.current);
    const deletes = new Set(pendingDeletes.current);

    pendingInserts.current.clear();
    pendingUpdates.current.clear();
    pendingDeletes.current.clear();

    // Process deletes immediately (no fetch needed)
    if (deletes.size > 0 && onDiscrepancyDelete) {
      deletes.forEach((id) => onDiscrepancyDelete(id));
    }

    // Combine inserts and updates for a single batch fetch
    const allIds = [...inserts.keys(), ...updates.keys()];
    if (allIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('discrepancies')
        .select(`
          *,
          projects (
            id,
            name,
            job_number,
            clients (name)
          ),
          contacts (
            id,
            name,
            email
          )
        `)
        .in('id', allIds);

      if (error) {
        console.error('Realtime: Error fetching discrepancy batch:', error);
        return;
      }

      if (data) {
        data.forEach((discrepancy) => {
          if (inserts.has(discrepancy.id) && onDiscrepancyInsert) {
            onDiscrepancyInsert(discrepancy);
          } else if (updates.has(discrepancy.id) && onDiscrepancyUpdate) {
            onDiscrepancyUpdate(discrepancy);
          }
        });
      }
    } catch (error) {
      console.error('Realtime: Discrepancy batch fetch failed:', error);
    }
  }, [onDiscrepancyUpdate, onDiscrepancyInsert, onDiscrepancyDelete]);

  // Queue an event for batched processing
  const queueEvent = useCallback(
    (payload) => {
      if (payload.eventType === 'INSERT' && payload.new && onDiscrepancyInsert) {
        pendingInserts.current.set(payload.new.id, payload);
      } else if (payload.eventType === 'UPDATE' && payload.new && onDiscrepancyUpdate) {
        pendingUpdates.current.set(payload.new.id, payload);
      } else if (payload.eventType === 'DELETE' && payload.old && onDiscrepancyDelete) {
        pendingDeletes.current.add(payload.old.id);
      }

      // Reset the batch timer
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      batchTimeout.current = setTimeout(processBatch, BATCH_DELAY_MS);
    },
    [processBatch, onDiscrepancyUpdate, onDiscrepancyInsert, onDiscrepancyDelete],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Subscribe to all changes on the discrepancies table
    const channel = supabase
      .channel('discrepancies-realtime')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'discrepancies'
        },
        queueEvent
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.error('Realtime: Failed to subscribe to discrepancies');
        }
      });

    // Cleanup subscription on unmount
    return () => {
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      supabase.removeChannel(channel);
    };
  }, [enabled, queueEvent]);
};